/** Form primitives. Labels, hints, and validation messages in one place. */

import { useId, useLayoutEffect, useRef, useState } from 'react';
import type { ChangeEvent, ReactNode } from 'react';

import { groupDigits } from '../lib/listing-preview';

interface BaseProps {
  readonly label: string;
  readonly hint?: ReactNode;
  readonly error?: string | undefined;
  readonly required?: boolean;
}

/**
 * The label line.
 *
 * A counter — characters used, items chosen — belongs on the same line as the
 * label rather than under the control, where it would push the hint down and
 * shift the field every time a character is typed.
 */
function FieldTop({
  id,
  label,
  required,
  counter,
}: {
  readonly id: string;
  readonly label: string;
  readonly required?: boolean;
  readonly counter?: ReactNode;
}): ReactNode {
  const heading = (
    <label htmlFor={id}>
      {label}
      {required === true ? <span aria-hidden="true"> *</span> : null}
    </label>
  );
  if (counter === undefined) return heading;
  return (
    <div className="fieldtop">
      {heading}
      <span className="counter mono" aria-hidden="true">
        {counter}
      </span>
    </div>
  );
}

interface TextFieldProps extends BaseProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly type?: 'text' | 'email' | 'password' | 'url' | 'number';
  readonly placeholder?: string;
  readonly maxLength?: number;
  readonly autoComplete?: string;
  readonly inputMode?: 'text' | 'numeric' | 'email';
  readonly disabled?: boolean;
  readonly className?: string;
  /** Show a `58 / 140` count beside the label. Needs `maxLength`. */
  readonly showCount?: boolean;
}

export function TextField(props: TextFieldProps): ReactNode {
  const id = useId();
  const describedBy = props.error !== undefined ? `${id}-error` : undefined;
  const counter =
    props.showCount === true && props.maxLength !== undefined
      ? `${props.value.length} / ${props.maxLength}`
      : undefined;
  return (
    <div className="field">
      <FieldTop
        id={id}
        label={props.label}
        {...(props.required === undefined ? {} : { required: props.required })}
        {...(counter === undefined ? {} : { counter })}
      />
      <input
        id={id}
        type={props.type ?? 'text'}
        className={props.className}
        value={props.value}
        placeholder={props.placeholder}
        maxLength={props.maxLength}
        autoComplete={props.autoComplete}
        inputMode={props.inputMode}
        disabled={props.disabled}
        aria-invalid={props.error !== undefined}
        aria-describedby={describedBy}
        onChange={(event: ChangeEvent<HTMLInputElement>) => props.onChange(event.target.value)}
      />
      {props.hint === undefined ? null : <span className="hint">{props.hint}</span>}
      {props.error === undefined ? null : (
        <span className="error" id={`${id}-error`}>
          {props.error}
        </span>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Money                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * A salary figure, grouped as it is typed.
 *
 * The currency symbol is chrome inside the control rather than a character in
 * the value, and the caret is held at its distance from the right-hand end, so
 * inserting a digit that pushes a new thousands separator in does not throw
 * the cursor a place to the left.
 */
export function MoneyField(props: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly symbol?: string;
  readonly placeholder?: string;
  readonly hint?: ReactNode;
  readonly error?: string | undefined;
  readonly required?: boolean;
  readonly invalid?: boolean;
  readonly onBlur?: () => void;
}): ReactNode {
  const id = useId();
  const input = useRef<HTMLInputElement | null>(null);
  const caret = useRef<number | undefined>(undefined);

  useLayoutEffect(() => {
    const element = input.current;
    const fromRight = caret.current;
    if (element === null || fromRight === undefined) return;
    caret.current = undefined;

    // Walk back from the end until the right number of digits have been passed.
    const text = element.value;
    let seen = 0;
    let position = text.length;
    while (position > 0 && seen < fromRight) {
      position -= 1;
      if (/\d/.test(text[position] as string)) seen += 1;
    }
    element.setSelectionRange(position, position);
  }, [props.value]);

  function change(event: ChangeEvent<HTMLInputElement>): void {
    const element = event.target;
    const cursor = element.selectionStart ?? element.value.length;
    caret.current = element.value.slice(cursor).replace(/\D/g, '').length;
    props.onChange(groupDigits(element.value));
  }

  const classes = ['moneyfield'];
  if (props.invalid === true) classes.push('is-wanting');

  return (
    <div className="field">
      <FieldTop
        id={id}
        label={props.label}
        {...(props.required === undefined ? {} : { required: props.required })}
      />
      <div className={classes.join(' ')}>
        <span className="prefix" aria-hidden="true">
          {props.symbol ?? '$'}
        </span>
        <input
          id={id}
          ref={input}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={props.value}
          placeholder={props.placeholder}
          aria-invalid={props.error !== undefined}
          onChange={change}
          onBlur={props.onBlur}
        />
      </div>
      {props.hint === undefined ? null : <span className="hint">{props.hint}</span>}
      {props.error === undefined ? null : <span className="error">{props.error}</span>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Stepper                                                                    */
/* -------------------------------------------------------------------------- */

export function MinusIcon(): ReactNode {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
      <path d="M2 5h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export function PlusIcon(): ReactNode {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
      <path d="M5 2v6M2 5h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/** A whole-number field with the two buttons that step it. */
export function StepperField(props: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly min?: number;
  readonly max?: number;
  readonly hint?: ReactNode;
  readonly error?: string | undefined;
}): ReactNode {
  const id = useId();
  const min = props.min ?? 1;
  const max = props.max ?? 9999;
  const current = Number.parseInt(props.value.replace(/\D/g, ''), 10);
  const number = Number.isFinite(current) ? current : min;

  function step(delta: number): void {
    props.onChange(String(Math.min(max, Math.max(min, number + delta))));
  }

  return (
    <div className="field">
      <FieldTop id={id} label={props.label} />
      <div className="stepperfield">
        <input
          id={id}
          type="text"
          inputMode="numeric"
          className="mono"
          value={props.value}
          aria-invalid={props.error !== undefined}
          onChange={(event) => props.onChange(event.target.value)}
        />
        <span className="steps-x">
          <button
            type="button"
            className="stepbtn"
            aria-label={`Decrease ${props.label.toLowerCase()}`}
            disabled={number <= min}
            onClick={() => step(-1)}
          >
            <MinusIcon />
          </button>
          <button
            type="button"
            className="stepbtn"
            aria-label={`Increase ${props.label.toLowerCase()}`}
            disabled={number >= max}
            onClick={() => step(1)}
          >
            <PlusIcon />
          </button>
        </span>
      </div>
      {props.hint === undefined ? null : <span className="hint">{props.hint}</span>}
      {props.error === undefined ? null : <span className="error">{props.error}</span>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Chips                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * A comma-separated list, shown as the chips it will become.
 *
 * The value on the wire is still one comma-separated string — the form state
 * and the validator are untouched — but a typed comma settles the word into a
 * chip, so twenty skills read as twenty things rather than one long line.
 */
export function ChipsField(props: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly hint?: ReactNode;
  readonly limit: number;
}): ReactNode {
  const id = useId();
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);

  const chips = props.value
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '');

  function commit(next: readonly string[]): void {
    props.onChange(next.join(', '));
  }

  function add(raw: string): void {
    const entry = raw.trim();
    if (entry === '' || chips.length >= props.limit) return;
    if (chips.some((chip) => chip.toLowerCase() === entry.toLowerCase())) return;
    commit([...chips, entry]);
  }

  function typed(raw: string): void {
    if (!raw.includes(',')) {
      setDraft(raw);
      return;
    }
    const parts = raw.split(',');
    const tail = parts.pop() ?? '';
    const kept = [...chips];
    for (const part of parts) {
      const entry = part.trim();
      if (entry === '' || kept.length >= props.limit) continue;
      if (kept.some((chip) => chip.toLowerCase() === entry.toLowerCase())) continue;
      kept.push(entry);
    }
    commit(kept);
    setDraft(tail);
  }

  return (
    <div className="field">
      <FieldTop id={id} label={props.label} counter={`${chips.length} / ${props.limit}`} />
      {/* The frame is the control: clicking anywhere in it focuses the entry. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <div
        className={focused ? 'chipsfield is-focus' : 'chipsfield'}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            event.preventDefault();
            document.getElementById(id)?.focus();
          }
        }}
      >
        {chips.map((chip) => (
          <span className="chip" key={chip}>
            {chip}
            <button
              type="button"
              className="chip-x"
              aria-label={`Remove ${chip}`}
              onClick={() => commit(chips.filter((entry) => entry !== chip))}
            >
              <svg width="9" height="9" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path
                  d="M1.5 1.5l7 7M8.5 1.5l-7 7"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </span>
        ))}
        <input
          id={id}
          type="text"
          value={draft}
          placeholder={chips.length === 0 ? props.placeholder : ''}
          onChange={(event) => typed(event.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            add(draft);
            setDraft('');
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              add(draft);
              setDraft('');
            }
            if (event.key === 'Backspace' && draft === '' && chips.length > 0) {
              commit(chips.slice(0, -1));
            }
          }}
        />
      </div>
      {props.hint === undefined ? null : <span className="hint">{props.hint}</span>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

interface TextAreaProps extends BaseProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly rows?: number;
  readonly placeholder?: string;
  /**
   * A cap the field actually enforces. Without it a hint reading "0 of 2000
   * characters" is a limit the browser lets you sail past, to be refused by the
   * server after the writing is done.
   */
  readonly maxLength?: number;
}

export function TextAreaField(props: TextAreaProps): ReactNode {
  const id = useId();
  return (
    <div className="field">
      <FieldTop
        id={id}
        label={props.label}
        {...(props.required === undefined ? {} : { required: props.required })}
      />
      <textarea
        id={id}
        rows={props.rows}
        maxLength={props.maxLength}
        value={props.value}
        placeholder={props.placeholder}
        aria-invalid={props.error !== undefined}
        onChange={(event) => props.onChange(event.target.value)}
      />
      {props.hint === undefined ? null : <span className="hint">{props.hint}</span>}
      {props.error === undefined ? null : <span className="error">{props.error}</span>}
    </div>
  );
}

interface SelectFieldProps<T extends string> extends BaseProps {
  readonly value: T | '';
  readonly onChange: (value: T) => void;
  readonly options: readonly { readonly value: T; readonly label: string }[];
  readonly placeholder?: string;
  readonly disabled?: boolean;
}

export function SelectField<T extends string>(props: SelectFieldProps<T>): ReactNode {
  const id = useId();
  return (
    <div className="field">
      <FieldTop
        id={id}
        label={props.label}
        {...(props.required === undefined ? {} : { required: props.required })}
      />
      <select
        id={id}
        value={props.value}
        disabled={props.disabled}
        aria-invalid={props.error !== undefined}
        onChange={(event) => props.onChange(event.target.value as T)}
      >
        {props.placeholder === undefined ? null : <option value="">{props.placeholder}</option>}
        {props.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {props.hint === undefined ? null : <span className="hint">{props.hint}</span>}
      {props.error === undefined ? null : <span className="error">{props.error}</span>}
    </div>
  );
}

interface CheckboxGroupProps<T extends string> {
  readonly legend: string;
  readonly options: readonly { readonly value: T; readonly label: string }[];
  readonly selected: readonly T[];
  readonly onChange: (values: T[]) => void;
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
  /** Show only this many options until the company asks for the rest. */
  readonly collapseAfter?: number;
  readonly boxed?: boolean;
}

export function CheckboxGroup<T extends string>(props: CheckboxGroupProps<T>): ReactNode {
  const [expanded, setExpanded] = useState(false);
  const limit = props.collapseAfter;
  const collapsible = limit !== undefined && props.options.length > limit;
  const shown = collapsible && !expanded ? props.options.slice(0, limit) : props.options;

  return (
    <fieldset className={props.boxed === true ? 'boxed' : undefined}>
      <legend>{props.legend}</legend>
      {props.hint === undefined ? null : <span className="hint">{props.hint}</span>}
      <div className="checkbox-grid">
        {shown.map((option) => {
          const checked = props.selected.includes(option.value);
          return (
            <label key={option.value} className={checked ? 'is-on' : undefined}>
              <input
                type="checkbox"
                checked={checked}
                onChange={() =>
                  props.onChange(
                    checked
                      ? props.selected.filter((value) => value !== option.value)
                      : [...props.selected, option.value],
                  )
                }
              />
              {option.label}
            </label>
          );
        })}
      </div>
      {collapsible && !expanded ? (
        <button type="button" className="linkbtn morelink" onClick={() => setExpanded(true)}>
          Show all {props.options.length}
        </button>
      ) : null}
      {props.error === undefined ? null : <span className="error">{props.error}</span>}
    </fieldset>
  );
}
